import type { DiscountCode } from "@graphql/generated/types";
import { useGetOffers } from "@hooks/useGetOffers";
import { CommonCMS } from "@lib/constants/contentful";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { editDiscountKey } from "@lib/utils/cart-utility/cartUtility";
import { Text } from "@ecco/ui/text";
import { Money } from "@ecco/ui/money";
import type { SimpleMoneyData } from "@ecco/ui/use-money";
import { Spinner } from "@ecco/ui/spinner";
import { Button } from "@ecco/ui/button";
import { Flex } from "@ecco/ui/flex";
import { removeCountryPrefix } from "../../utils/removeCountryPrefix";
import { useEffect } from "react";
import { getPrivacyAttributes } from "@lib/monitoring/privacyAttributes";

type Props = {
    isRemoving: boolean;
    onRemove: (discountId: string) => void;
    countryISOCode: string;
    headers: {
        remove: string;
        removeAria: string;
    };
    id: string;
    errorMessage?: string;
    isPromoRemoving?: boolean;
    promoCode: DiscountCode;
    locale: string;
    discountAmount: SimpleMoneyData;
    isLocked: boolean;
    isDuplicatePromoCode: boolean;
    setAddedPromocodesErrors: (errors: { id?: string; message?: string; code?: string }[]) => void;
};

export const PromoCode = ({
    isRemoving,
    locale,
    id,
    isLocked,
    promoCode,
    onRemove,
    headers,
    discountAmount,
    isDuplicatePromoCode,
    setAddedPromocodesErrors,
    countryISOCode,
}: Props) => {
    const { get: getMicrocopy } = useMicrocopy();
    const { remove, removeAria } = headers;
    const { name, key, code } = promoCode;

    const offers = useGetOffers({ offerIds: [editDiscountKey(key, "_")] });
    const description = offers[0]?.description;
    useEffect(() => {
        if (isDuplicatePromoCode) {
            setAddedPromocodesErrors?.([
                {
                    id: promoCode?.id,
                    code: removeCountryPrefix(promoCode?.code, countryISOCode),
                    message: getMicrocopy(CommonCMS.shared, CommonCMS.promoCode.doublePromoCode),
                },
            ]);
            onRemove(id);
        }
    }, [isDuplicatePromoCode]);

    return (
        <Flex flexDirection={"row"} gap={4} justifyContent={"space-between"}>
            <Flex gap={1} direction="column" w="full">
                {name && <Text fontSize="desktopBodyTextSmall">{name}</Text>}
                {description && <Text fontSize="desktopBodyTextSmall">{description}</Text>}
                <Flex justifyContent="space-between" alignItems="center">
                    <Text
                        {...getPrivacyAttributes()}
                        fontWeight="bold"
                        fontSize="desktopBodyTextSmall"
                    >
                        {code?.slice(2)}
                    </Text>
                    {discountAmount && (
                        <Text display="flex" fontWeight="bold" color="gray.500">
                            -<Money as="span" data={discountAmount} locale={locale} />
                        </Text>
                    )}
                </Flex>
                {isRemoving ? (
                    <Spinner size={"sm"} />
                ) : (
                    <Button
                        isDisabled={isLocked}
                        p={0}
                        px={0}
                        variant="ghost"
                        w="fit-content"
                        h="fit-content"
                        mt={1}
                        fontSize={"desktopBodyTextSmall"}
                        fontWeight={"semibold"}
                        cursor={"pointer"}
                        color={"gray.500"}
                        textDecor={"underline"}
                        aria-label={removeAria}
                        onClick={() => onRemove(id)}
                    >
                        {remove}
                    </Button>
                )}
            </Flex>
        </Flex>
    );
};
